import React from "react";
import RevealWords, { RevealTiming, RevealWordsTiming, revealStyle } from "../../../components/Reveal/RevealWords";
import DashedArrow from "../../../components/DashedArrow/DashedArrow";

export type StepStatus = "done" | "active" | "upcoming";

export interface StepTrackerItem {
  status: StepStatus;
  content: React.ReactNode;
  /** Entrance of the circle and of the connector under it; omit for no animation. */
  reveal?: { dot: RevealTiming; connector?: RevealTiming };
}

interface StepTrackerProps {
  steps: StepTrackerItem[];
  compact: boolean;
}

const StepTracker: React.FC<StepTrackerProps> = ({ steps, compact }) => (
  <div className="flex flex-col">
    {steps.map((step, i) => {
      const isLast = i === steps.length - 1;
      const circle = compact ? "w-5 h-5" : "w-6 h-6 2xl:w-[28.8px] 2xl:h-[28.8px]";
      // Short connectors keep the 3-step rail + CTA inside one desktop viewport.
      // min-h + flex-1: the rail stretches with a tall step (the Roadmap card)
      // so it reaches the next circle instead of stopping mid-card.
      const connector = compact ? "min-h-[52px]" : "min-h-8 xl:min-h-10 2xl:min-h-12";
      return (
        <div key={i} className="flex gap-4 lg:gap-4 2xl:gap-[19.2px]">
          <div className="flex flex-col items-center shrink-0">
            <div
              className={`${circle} rounded-full bg-white flex items-center justify-center shrink-0 ${step.reveal ? "reveal-fade" : ""}`}
              style={{
                border: `2.4px solid ${step.status === "upcoming" ? "#dee3ed" : "#ffb909"}`,
                ...(step.reveal && revealStyle(step.reveal.dot)),
              }}
            >
              {step.status === "active" && (
                <div
                  className="rounded-full"
                  style={{
                    width: compact ? 10 : 12,
                    height: compact ? 10 : 12,
                    backgroundImage: "linear-gradient(90deg, #ffb909, #e17200)",
                  }}
                />
              )}
              {step.status === "done" && (
                <svg width={compact ? 10 : 12} height={compact ? 10 : 12} viewBox="0 0 14 14" fill="none">
                  <path d="M2 7.2l3 3L12 3" stroke="#e17200" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              )}
            </div>
            {!isLast &&
              (step.reveal?.connector ? (
                <DashedArrow
                  className={`${connector} flex-1 py-1 reveal-draw-down`}
                  style={revealStyle(step.reveal.connector)}
                />
              ) : (
                <div
                  className={`${connector} flex-1 w-[2.4px]`}
                  style={{ backgroundImage: "linear-gradient(180deg, #ffb909, #dee3ee)" }}
                />
              ))}
          </div>
          {/* 16px under a step so a tall card doesn't crowd the next one. */}
          <div className={`flex flex-col ${isLast ? "" : "pb-4"} flex-1 min-w-0`}>{step.content}</div>
        </div>
      );
    })}
  </div>
);

interface StepLabelTitleProps {
  label: string;
  title: string;
  active: boolean;
  compact: boolean;
  done?: boolean;
  reveal?: { label: RevealWordsTiming; title: RevealWordsTiming };
}

const TITLE_GRADIENT: React.CSSProperties = {
  backgroundImage: "linear-gradient(90deg, #ffb909, #e17200)",
  WebkitBackgroundClip: "text",
  WebkitTextFillColor: "transparent",
};

export const StepLabelTitle: React.FC<StepLabelTitleProps> = ({ label, title, active, compact, done, reveal }) => {
  const highlighted = active || done;
  return (
    <div className="flex flex-col">
      <span
        className={`font-['Poppins'] font-medium uppercase ${compact ? "text-[13px]" : "text-[13px] 2xl:text-[14.4px]"} leading-tight`}
        style={{ color: highlighted ? "#f59301" : "#99a3b2" }}
      >
        {reveal ? <RevealWords text={label} timing={reveal.label} /> : label}
      </span>
      {highlighted ? (
        <span
          className={`font-['Poppins'] font-medium ${compact ? "text-[18px]" : "text-[20px] 2xl:text-[22px]"} leading-snug bg-clip-text`}
          style={reveal ? undefined : TITLE_GRADIENT}
        >
          {reveal ? <RevealWords text={title} timing={reveal.title} wordStyle={TITLE_GRADIENT} /> : title}
        </span>
      ) : (
        <span className={`font-['Poppins'] font-medium ${compact ? "text-[18px]" : "text-[20px] 2xl:text-[22px]"} leading-snug text-[#333]`}>
          {reveal ? <RevealWords text={title} timing={reveal.title} /> : title}
        </span>
      )}
    </div>
  );
};

export default StepTracker;
